import React, { useState, useCallback, useEffect } from 'react'
import { Box, Text, useApi, DataTable } from '@fivehealth/botero'
import { format, parseISO } from 'date-fns'
import { isEqual, isEmpty, startCase, trim, chain } from 'lodash'
import { DateTime } from 'luxon'
import { useQueryClient } from 'react-query'
import { AlertModalConfirmation } from './helper'
import EventsConstant from '../../config/constants/events.constants'
import { EXPORT_GQL_DOC as EXPORT_DEPARTMENTS } from '../../api/queries/useDepartments'
import SettingsTableRow from './components/SettingsTableRowEdit'
import SettingsSearch from './components/SettingsSearch'
import ProgressAnimation from '../BotAnalytics/components/ProgressAnimation'
import { useModal } from '../../context/ModalContext'
import AddDepartmentDesignationModal from '../../components/Modals/AddDepartmentDesignationModal'
import BotmdCryBaby from '../../assets/bot-crying-avatar.svg'

const getSortId = (columnId) => {
  switch (columnId) {
    case 'createdOn':
      return 'CREATED_ON'
    case 'name':
      return 'NAME'
    case 'createdBy':
      return 'CREATED_BY'
    default:
      return columnId
  }
}

const DepartmentTable = ({
  invalidateSettings,
  partialEditModalProps,
  partialDeleteModalProps,
  setExportOptions,
  exportFileUsingLink,
  setAddOptions,
  setDepSearchQuery,
  setSelectedTabCount,
  tab,
}) => {
  const INIT_DEFAULT_FETCH = 25
  const DATE_FORMAT = 'dd MMMM yyyy, h:mm a'
  const DEPARTMENT = 'department'
  const { client } = useApi()
  const [searchQuery, setSearchQuery] = useState('')
  const [allDepartmentData, setAllDepartmentData] = useState([])
  const [sortParams, setSortParams] = useState({
    id: 'createdOn',
    desc: true,
  })
  const { openModal, closeModal } = useModal()
  const {
    queries: { useDepartments },
  } = useApi({
    queries: ['useDepartments'],
  })
  const queryClient = useQueryClient()

  const getExportDepartmentsURL = async (outputFormat) => {
    try {
      const res = await client.request(EXPORT_DEPARTMENTS, {
        outputFormat,
        search: searchQuery,
      })
      return res?.hospitalDepartments?.exportByUrl
    } catch (e) {
      return false
    }
  }
  const ExportOptions = {
    value: DEPARTMENT,
    label: 'All departments',
    query: getExportDepartmentsURL,
    handleExportData: async (fileType) => {
      const exportUrl = await ExportOptions?.query(fileType)
      const fileName = `WorkspaceSetting${ExportOptions?.label?.replaceAll(
        ' ',
        ''
      )}`
      exportFileUsingLink(exportUrl, fileName)
    },
    buttonLable: 'Export Data',
  }

  const { mutateAsync: createDepartmentMutation } = useDepartments().create({
    variables: {},
    onSuccess: (response) => {
      queryClient.invalidateQueries('allDepartments')
      openModal(
        <AlertModalConfirmation
          logEventProps={{
            subSource: EventsConstant.WORKSPACE_SETTINGS_LIST,
            eventName: EventsConstant.DEPARTMENT_CREATED_SUCCESS,
            page: EventsConstant.WORKSPACE_SETTINGS_PAGE,
          }}
          title="Department has been created"
          description={format(
            parseISO(
              response?.data?.hospitalDepartmentCreate?.hospitalDepartment
                ?.createdOn
            ),
            DATE_FORMAT
          )}
          closeModal={() => {
            closeModal()
            invalidateSettings(DEPARTMENT)
          }}
        />
      )
    },
  })

  useEffect(() => {
    setExportOptions(null)
    setAddOptions(null)
    setExportOptions(ExportOptions)
    setAddOptions({
      func: () =>
        openModal(
          <AddDepartmentDesignationModal
            type={DEPARTMENT}
            handleClose={closeModal}
            createDepartmentMutation={createDepartmentMutation}
          />
        ),
      label: 'Add Department',
    })
  }, [])
  const {
    data: dataDepartments,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingDepts,
  } = useDepartments().paginated({
    variables: {
      sortBy: getSortId(sortParams.id),
      sortDesc: sortParams.desc,
      first: INIT_DEFAULT_FETCH,
      search: searchQuery,
      outputFormat: 'CSV',
    },
  })
  useEffect(() => {
    const tempTasks = chain(dataDepartments)
      .get('pages', [])
      .flatMap((page) => page || [])
      .map((task) => task)
      .value()
    setAllDepartmentData(tempTasks)
  }, [dataDepartments])
  useEffect(() => {
    setSelectedTabCount({
      isFetching: isLoadingDepts,
      count: dataDepartments?.totalCount,
      tab,
    })
  }, [dataDepartments, fetchNextPage])

  const { mutateAsync: updateDepartmentMutation } = useDepartments().update({
    variables: {},
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries('allDepartments')
      let isError = false
      let modalProps = null
      if (data?.errors?.length > 0) {
        modalProps = {
          title: 'Error in renaming department',
          description: data.errors[0]?.message.includes('Invalid input.')
            ? 'Invalid input.'
            : 'Department with same name already exists. Please rename and try again.',
          botmdImage: BotmdCryBaby,
        }
        isError = true
      } else {
        modalProps = {
          title: 'Department has been renamed',
          description: format(
            parseISO(
              data?.hospitalDepartmentUpdate?.hospitalDepartment.updatedOn
            ),
            DATE_FORMAT
          ),
        }
      }

      openModal(
        <AlertModalConfirmation
          logEventProps={{
            subSource: EventsConstant.WORKSPACE_SETTINGS_LIST,
            eventName: isError
              ? EventsConstant.DEPARTMENT_RENAMED_FAILED
              : EventsConstant.DEPARTMENT_RENAMED_SUCCESS,
            page: EventsConstant.WORKSPACE_SETTINGS_PAGE,
          }}
          closeModal={() => {
            closeModal()
            invalidateSettings(DEPARTMENT)
          }}
          {...modalProps}
        />
      )
    },
  })

  const { mutateAsync: deleteDepartmentMutation } = useDepartments().delete({
    variables: {},
    onSuccess: () => {
      queryClient.invalidateQueries('allDepartments')
      openModal(
        <AlertModalConfirmation
          logEventProps={{
            subSource: EventsConstant.WORKSPACE_SETTINGS_LIST,
            eventName: EventsConstant.DEPARTMENT_DELETED_SUCCESS,
            page: EventsConstant.WORKSPACE_SETTINGS_PAGE,
          }}
          title="Department successfully deleted"
          description={format(new Date(), DATE_FORMAT)}
          closeModal={() => {
            closeModal()
            invalidateSettings(DEPARTMENT)
          }}
        />
      )
    },
  })

  const { mutateAsync: createDepartmentMutationNoModal } =
    useDepartments().create({
      variables: {},
    })

  const onDepartmentDeleteHandler = async ({
    uid,
    reassignName,
    createNewTitle,
  }) => {
    let newDepartmentName = null
    if (createNewTitle) {
      const createResponse = await createDepartmentMutationNoModal({
        create: { name: trim(createNewTitle) },
      })
      newDepartmentName =
        createResponse?.hospitalDepartmentCreate?.hospitalDepartment?.name
    }
    if (newDepartmentName || reassignName) {
      const replaceName = reassignName || newDepartmentName
      deleteDepartmentMutation({
        delete: {
          uid,
          replaceName,
        },
      })
    } else {
      deleteDepartmentMutation({
        delete: {
          uid,
        },
      })
    }
  }

  const createColumns = useCallback((departmentData) => {
    const department = departmentData
    if (!isEmpty(department)) {
      const result = ['name', 'createdOn', 'createdBy'].map((key) => ({
        id: key,
        accessor: key === 'createdBy' ? 'createdBy.firstName' : key,
        Header: startCase(key),
        disableSortBy: false,
        Cell: (data) => {
          if (isEqual(key, 'createdOn') || isEqual(key, 'updatedOn')) {
            return (
              <Text>
                {data.value
                  ? DateTime.fromISO(data.value)
                      .setLocale('en-SG')
                      .toFormat('dd LLL yyyy, t')
                  : '-'}
              </Text>
            )
          }
          return <Text>{data.value}</Text>
        },
      }))
      return result
    }
    return []
  }, [])
  return (
    <Box mb={2} mt={3}>
      <SettingsSearch
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        setTypeSearchQuery={setDepSearchQuery}
        searchLabel="Search by department name"
      />
      {isLoadingDepts || !allDepartmentData ? (
        <ProgressAnimation />
      ) : (
        <div>
          <DataTable
            columns={createColumns(allDepartmentData)}
            data={allDepartmentData}
            onFetchData={({ sortBy }) => {
              setSortParams(sortBy[0])
            }}
            initialSortBy={sortParams}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            enableRowEdit="true"
            onFetchNextPage={fetchNextPage}
            renderRowEdit={({ original }) => (
              <SettingsTableRow
                data={original}
                openModal={openModal}
                updateMutation={updateDepartmentMutation}
                partialEditModalProps={partialEditModalProps}
                typeCaps={DEPARTMENT}
                deleteMutation={onDepartmentDeleteHandler}
                partialDeleteModalProps={partialDeleteModalProps}
              />
            )}
          />
        </div>
      )}
    </Box>
  )
}

export default DepartmentTable
